import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Anchor,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Group,
  Stack,
  Table,
  Title,
} from "@mantine/core";
import { v4 as uuidv4 } from "uuid";

import {
  ADMIN_ORGANIZATION,
  BINGO_CREATE_AUTHORISED_ORGANIZATIONS,
  BINGO_CREATE_AUTHORISED_ROLES,
  BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
} from "_roles";

import type { BingoTemplate } from "bingo/types/bingo-template.model";
import { AccessRights } from "common/admin/auth/AccessRights";
import { useOrganization } from "common/admin/auth/hooks";
import QueryWrapper from "common/components/QueryWrapper";
import { Th } from "common/components/table/Th";

import {
  useListBingoTemplatesQuery,
  useRemoveBingoTemplateMutation,
} from "bingo/api/bingo-template.api";
import { BingoTemplateAdminRow } from "bingo/components/BingoTemplateAdminRow";
import { ADMIN_BINGO_PATH, ADMIN_PATH } from "_urls";

type SortedKeys = keyof Pick<BingoTemplate, "creationTime" | "name" | "access">;

function sortData(
  data: BingoTemplate[],
  payload: { sortBy: SortedKeys; reversed: boolean },
): BingoTemplate[] {
  return [...data].sort((a, b) => {
    if (payload.reversed) {
      return String(b[payload.sortBy]).localeCompare(String(a[payload.sortBy]));
    }
    return String(a[payload.sortBy]).localeCompare(String(b[payload.sortBy]));
  });
}

export function BingoTemplatesAdminListView() {
  const navigate = useNavigate();
  const listBingoTemplatesQuery = useListBingoTemplatesQuery();
  const [sortedData, setSortedData] = useState<BingoTemplate[]>([]);
  const [sortBy, setSortBy] = useState<SortedKeys>("creationTime");
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [organization] = useOrganization();

  const bingoTemplates = useMemo(
    () => listBingoTemplatesQuery.data,
    [listBingoTemplatesQuery.data],
  );

  const filteredBingoTemplates = useMemo(
    () =>
      (bingoTemplates || []).filter(
        (bingoTemplate) => bingoTemplate.creationTime && bingoTemplate.name,
      ),
    [bingoTemplates],
  );

  const [deleteBingoTemplate] = useRemoveBingoTemplateMutation();

  const handleDelete = async (id: string) => {
    try {
      await deleteBingoTemplate(id).unwrap();
      console.log(`Bingo template ${id} deleted successfully`);
    } catch (error) {
      console.error("Failed to delete bingo template", error);
    }
  };

  const triggerSortingData = useCallback(
    (field: SortedKeys) => {
      const reversed = field === sortBy ? !reverseSortDirection : false;
      return sortData(filteredBingoTemplates, { sortBy, reversed });
    },
    [filteredBingoTemplates, reverseSortDirection, sortBy],
  );

  useEffect(() => {
    setSortedData(triggerSortingData(sortBy));
  }, [filteredBingoTemplates, sortBy, triggerSortingData]);

  return (
    <Box>
      <div
        className="sticky top-0 z-50 pb-4"
        style={{ backgroundColor: "#E9ECEF" }}
      >
        <Title order={3} align={"center"} m={"xs"}>
          Bingo templates
        </Title>
        <Breadcrumbs separator="→">
          <Anchor component={Link} to={`/${ADMIN_PATH}`}>
            Admin
          </Anchor>

          <Anchor component={Link} to={`/${ADMIN_PATH}/${ADMIN_BINGO_PATH}`}>
            Bingo
          </Anchor>

          <span>Bingo Templates</span>
        </Breadcrumbs>

        <AccessRights
          authorizedOrganizations={BINGO_CREATE_AUTHORISED_ORGANIZATIONS}
          authorizedRoles={BINGO_CREATE_AUTHORISED_ROLES}
        >
          <Group position={"right"}>
            <Button className="bg-button-admin" component={Link} to="new">
              New
            </Button>
          </Group>
        </AccessRights>
      </div>
      <Card shadow={"sm"} p={"xl"} className={"overflow-visible"}>
        <QueryWrapper query={listBingoTemplatesQuery}>
          {filteredBingoTemplates && filteredBingoTemplates.length > 0 ? (
            <Table highlightOnHover>
              <thead>
                <tr>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "name"}
                    onClick={() => {
                      if (sortBy === "name") {
                        setReverseSortDirection(!reverseSortDirection);
                      } else {
                        setSortBy("name");
                      }
                    }}
                  >
                    Name
                  </Th>
                  <AccessRights
                    authorizedOrganizations={
                      BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                    }
                    authorizedRoles={
                      BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES
                    }
                  >
                    <Th
                      reversed={reverseSortDirection}
                      sorted={sortBy === "access"}
                      onClick={() => {
                        if (sortBy === "access") {
                          setReverseSortDirection(!reverseSortDirection);
                        } else {
                          setSortBy("access");
                        }
                      }}
                    >
                      Organization
                    </Th>
                  </AccessRights>

                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "creationTime"}
                    onClick={() => {
                      if (sortBy === "creationTime") {
                        setReverseSortDirection(!reverseSortDirection);
                      } else {
                        setSortBy("creationTime");
                      }
                    }}
                  >
                    Creation Date
                  </Th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.length > 0 &&
                  sortedData.map((bingoTemplate) =>
                    (bingoTemplate.access.organization &&
                      bingoTemplate.access.organization === organization) ||
                    organization === ADMIN_ORGANIZATION ? (
                      <BingoTemplateAdminRow
                        key={uuidv4()}
                        bingoTemplate={bingoTemplate}
                        onEdit={() => navigate(bingoTemplate.id)}
                        onDelete={() => {
                          handleDelete(bingoTemplate.id);
                        }}
                        onDuplicate={() =>
                          navigate(`${bingoTemplate.id}/duplicate`)
                        }
                      />
                    ) : null,
                  )}
              </tbody>
            </Table>
          ) : (
            <Stack align={"center"}>
              <Box>Aucun Bingo template trouvé</Box>
              <Button className="bg-button-admin" component={Link} to="new">
                Ajouter un Bingo template
              </Button>
            </Stack>
          )}
        </QueryWrapper>
      </Card>
    </Box>
  );
}
